<script lang="ts" setup>
import { storeToRefs } from "pinia";
import DateInput from "~/components/Common/Inputs/DateInput.vue";
import TimePicker from "~/components/Common/Inputs/TimePicker.vue";
import SimpleDropdown from "~/components/Common/Dropdowns/SimpleDropdown.vue";
import LocationDropdown from "~/components/Common/Dropdowns/LocationDropdown.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import type { ItemTimeslots } from "@simbelapp/order-sdk";
import { SessionFormats } from "~/utils/options/session-items.options";
import { add } from "date-fns";
import FlatLoadButton from "~/components/Common/Buttons/FlatLoadButton.vue";
import TagDropdown from "~/components/Common/Dropdowns/TagDropdown.vue";
import { useUsersStore } from "~/store/users/users.store";
import type { UserCompanyModel } from "@simbelapp/order-sdk";
import { useCompanyLocationsStore } from "~/store/companies/locations.store";
import { useSessionDetailsTimeslotsStore } from "~/store/sessions/session-details-timeslots-store";
import CommonInput from "~/components/Common/Inputs/CommonInput.vue";
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import RadioInput from "~/components/Common/Inputs/RadioInput.vue";
import { SessionItemFormatEnum, VISIOCONFERENCE_LOCATION } from "~/utils/enums/session-items.enums";
import TimezoneDropdown from "~/components/Common/Dropdowns/TimezoneDropdown.vue";
import { DEFAULT_TIMEZONE } from "~/utils/helpers/timezones";
import { useAuthStore } from "~/store/auth/auth.store";
import { useCompanyUsersDropdown } from "~/composables/users/useCompanyUsersDropdown";

const usersStore = useUsersStore();
const authStore = useAuthStore();
const error = ref(false);
const sessionDetailsTimeslotsStore = useSessionDetailsTimeslotsStore();
const emit = defineEmits(["close"]);
const locationsStore = useCompanyLocationsStore();
const { locations } = storeToRefs(locationsStore);

const props = defineProps({
  timeslot: {
    type: Object as PropType<ItemTimeslots>,
    required: false,
  },
  asyncAction: {
    type: Function,
    required: true,
  },
  error: {
    type: Boolean,
    default: false,
  },
  tmpSessionTimeslots: {
    type: Object as PropType<any | null>,
    required: true,
  },
  create: {
    type: Boolean,
    require: false,
  },
  trainingDuration: {
    type: Number,
    require: true,
  },
  timeslotsDuration: {
    type: Number,
    require: true,
  },
});

const timeToSeconds = (time: string) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 3600 + minutes * 60;
};
const tmp: any = ref(null);
const init = ref(false);
const tmpLocation: any = computed({
  get() {
    if (init.value) {
      return tmp.value;
    }
    if (props.tmpSessionTimeslots?.roomId) {
      tmp.value = props.tmpSessionTimeslots.selectedRoom;
      return tmp.value;
    } else if (props.tmpSessionTimeslots?.locationId) {
      tmp.value = props.tmpSessionTimeslots.selectedLocation;
      return tmp.value;
    } else if (props.tmpSessionTimeslots?.location && typeof props.tmpSessionTimeslots?.location !== "object") {
      tmp.value = {
        locationName: props.tmpSessionTimeslots.location,
      };
      return tmp.value;
    }
  },
  set(value) {
    init.value = true;
    if (!value) {
      tmp.value = null;
    } else if (value.locationId) {
      tmp.value = value;
    } else if (value.roomId) {
      tmp.value = value;
    } else {
      tmp.value = {
        locationId: null,
        locationName: value,
        locationStreet: "",
        locationCity: "",
        locationState: "",
        locationPostalCode: "",
        locationCountry: "",
        locationAccess: "",
        locationManualAddress: "",
      };
    }
  },
});

onMounted(() => {
  if (props.timeslot) {
    sessionDetailsTimeslotsStore.tmpSessionTimeslots.sessionTimeslots = props.timeslot;
    sessionDetailsTimeslotsStore.tmpSessionTimeslots.timeslotId = props.timeslot.timeslotId;
  }
});

const startDateModel = computed({
  get() {
    if (props.tmpSessionTimeslots?.startDate) {
      return props.tmpSessionTimeslots.startDate;
    }
  },
  set(newValue: any) {
    if (props.tmpSessionTimeslots) {
      props.tmpSessionTimeslots.startDate = newValue;
      if (!props.tmpSessionTimeslots?.endDate || new Date(newValue) > new Date(props.tmpSessionTimeslots.endDate)) {
        props.tmpSessionTimeslots.endDate = newValue;
      }
    }
  },
});

const startTimeModel = computed({
  get() {
    if (props.tmpSessionTimeslots?.startTime) {
      return props.tmpSessionTimeslots.startTime;
    }
  },
  set(newValue: any) {
    if (props.tmpSessionTimeslots) {
      props.tmpSessionTimeslots.startTime = newValue;
      error.value = false;
    }
  },
});

const endDateModel = computed({
  get() {
    if (props.tmpSessionTimeslots?.endDate) {
      return props.tmpSessionTimeslots.endDate;
    }
  },
  set(newValue: any) {
    if (props.tmpSessionTimeslots) {
      props.tmpSessionTimeslots.endDate = newValue;
    }
  },
});

const endTimeModel = computed({
  get() {
    if (props.tmpSessionTimeslots?.endTime) {
      return props.tmpSessionTimeslots.endTime;
    }
  },
  set(newValue: any) {
    if (props.tmpSessionTimeslots) {
      props.tmpSessionTimeslots.endTime = newValue;
      error.value = false;
    }
  },
});

/**
 * Edit mode reads the zone stored on the step, never the editor's: a Paris admin fixing the location of a
 * Nouméa step must not move it by 10 hours.
 */
const timezoneModel = computed({
  get(): string | null {
    return props.tmpSessionTimeslots?.timezone ?? null;
  },
  set(newValue: string | null) {
    if (props.tmpSessionTimeslots) {
      props.tmpSessionTimeslots.timezone = newValue;
    }
  },
});

onMounted(() => {
  if (!props.tmpSessionTimeslots || props.tmpSessionTimeslots.timezone) return;

  // A new step starts on the planner's zone, an existing step without one reads as Paris: the planner's
  // zone there would move a step nobody meant to move.
  props.tmpSessionTimeslots.timezone = props.timeslot ? DEFAULT_TIMEZONE : authStore.userTimezone;
});

const trainingDurationHoursModel = computed({
  get() {
    if (props.tmpSessionTimeslots?.trainingDurationHours) {
      return props.tmpSessionTimeslots.trainingDurationHours;
    }
  },
  set(newValue: any) {
    if (props.tmpSessionTimeslots) {
      if (newValue < 0) {
        props.tmpSessionTimeslots.trainingDurationHours = 0;
      } else {
        props.tmpSessionTimeslots.trainingDurationHours = newValue;
      }
    }
  },
});

const format = computed({
  get() {
    if (props.tmpSessionTimeslots?.format) {
      return SessionFormats.find((x) => x.name === props.tmpSessionTimeslots?.format);
    }
  },
  set(newValue: any) {
    if (props.tmpSessionTimeslots) {
      props.tmpSessionTimeslots.format = newValue.name;
      if (newValue.name === SessionItemFormatEnum.ONLINE) {
        Object.assign(props.tmpSessionTimeslots, {
          ...props.tmpSessionTimeslots,
          location: null,
          videoCallUrl: null,
          locationId: null,
          roomId: null,
          selectedLocation: null,
          selectedRoom: null,
        });
      }
    }
  },
});

const start = ref<Date | null>(null);
const end = ref<Date | null>(null);

const errorTime = computed(
  () =>
    !!error.value &&
    ((startTimeModel.value && !endTimeModel.value) || (start.value && end.value && start.value > end.value)),
);

const save = async () => {
  if (startDateModel.value) {
    start.value = add(new Date(startDateModel.value), {
      hours: startTimeModel.value ? parseInt(startTimeModel.value.split(":")[0]) : undefined,
      minutes: startTimeModel.value ? parseInt(startTimeModel.value.split(":")[1]) : undefined,
    });
  }

  if (endDateModel.value) {
    end.value = add(new Date(endDateModel.value), {
      hours: endTimeModel.value ? parseInt(endTimeModel.value.split(":")[0]) : undefined,
      minutes: endTimeModel.value ? parseInt(endTimeModel.value.split(":")[1]) : undefined,
    });
  }
  if (
    !startDateModel.value ||
    !endDateModel.value ||
    !trainingDurationHoursModel.value ||
    (startTimeModel.value && !endTimeModel.value) ||
    (!startTimeModel.value && endTimeModel.value) ||
    (startTimeModel.value && endTimeModel.value && start.value && end.value && start.value > end.value) ||
    (isVisioconference.value && !isValidUrl(props.tmpSessionTimeslots?.videoCallUrl))
  ) {
    error.value = true;
  } else {
    if (!isVisioconference.value) {
      if (tmpLocation.value?.roomId) {
        props.tmpSessionTimeslots.roomId = tmpLocation.value.roomId;
        props.tmpSessionTimeslots.locationId = tmpLocation.value.roomLocationId;
        if (locations.value.length > 0) {
          props.tmpSessionTimeslots.location = locations.value.find(
            (x: any) => x.locationId === tmpLocation.value.roomLocationId,
          )?.locationManualAddress;
        }
      } else if (tmpLocation.value?.locationId) {
        props.tmpSessionTimeslots.locationId = tmpLocation.value.locationId;
        props.tmpSessionTimeslots.location = tmpLocation.value.locationManualAddress;
        props.tmpSessionTimeslots.roomId = null;
        props.tmpSessionTimeslots.selectedRoom = null;
      } else {
        props.tmpSessionTimeslots.location = tmpLocation.value?.locationName ?? null;
        props.tmpSessionTimeslots.locationId = null;
        props.tmpSessionTimeslots.roomId = null;
        props.tmpSessionTimeslots.selectedLocation = null;
        props.tmpSessionTimeslots.selectedRoom = null;
      }

      //company-catalog
      if (!props.timeslot) {
        if (tmpLocation.value?.locationId) {
          props.tmpSessionTimeslots.locationId = tmpLocation.value.locationId;
          props.tmpSessionTimeslots.location = tmpLocation.value.locationManualAddress;
          props.tmpSessionTimeslots.selectedLocation = tmpLocation.value;
        } else if (tmpLocation.value?.roomId) {
          props.tmpSessionTimeslots.roomId = tmpLocation.value.roomId;
          props.tmpSessionTimeslots.locationId = tmpLocation.value.roomLocationId;
          props.tmpSessionTimeslots.location = locations.value.find(
            (x: any) => x.locationId === tmpLocation.value.roomLocationId,
          )?.locationManualAddress;
          props.tmpSessionTimeslots.selectedLocation = locations.value.find(
            (x: any) => x.locationId === tmpLocation.value.roomLocationId,
          );
          props.tmpSessionTimeslots.selectedRoom = tmpLocation.value;
        } else {
          props.tmpSessionTimeslots.location = tmpLocation.value?.locationName ?? null;
        }
      }
    }

    await props.asyncAction();
  }
};
const close = () => {
  emit("close");
};

const stepName = computed({
  get() {
    if (props.tmpSessionTimeslots?.name) {
      return props.tmpSessionTimeslots.name;
    }
  },
  set(newValue: any) {
    if (props.tmpSessionTimeslots) {
      props.tmpSessionTimeslots.name = newValue;
    }
  },
});

const newInternalTrainer = computed({
  get() {
    props.tmpSessionTimeslots.trainersUsersId = props.tmpSessionTimeslots.internalTrainers?.map((x: any) => x.userId);

    return props.tmpSessionTimeslots.internalTrainers?.map((x: any) => {
      return { userId: x.userId, displayValue: x.displayValue };
    });
  },
  set(newValue: any) {
    if (props.tmpSessionTimeslots) {
      props.tmpSessionTimeslots.internalTrainers = newValue.map((x: UserCompanyModel) => {
        return { userId: x.userId, displayValue: x.displayValue };
      });

      props.tmpSessionTimeslots.trainersUsersId = newValue.map((x: UserCompanyModel) => x.userId);
    }
  },
});

const locationsServicesAPI = (params: any) => locationsStore.searchRoomsAndLocations(params);

const { companyUsersServicesAPI } = useCompanyUsersDropdown();

const isVisioconference = computed(() => {
  return props.tmpSessionTimeslots?.location === VISIOCONFERENCE_LOCATION;
});

const isValidUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
};

const updateLocationType = (locationType: string | null) => {
  props.tmpSessionTimeslots.location = locationType;

  if (!locationType) {
    props.tmpSessionTimeslots.videoCallUrl = null;
  } else if (locationType === VISIOCONFERENCE_LOCATION) {
    Object.assign(props.tmpSessionTimeslots, {
      ...props.tmpSessionTimeslots,
      locationId: null,
      roomId: null,
      selectedLocation: null,
      selectedRoom: null,
    });
  }
};
</script>

<template>
  <div class="edit_card__container" :data-error="props.error">
    <div class="step__container">
      <div class="dates__collective_form">
        <!-- The third column wraps below 240 px (the catalogue wizard) rather than being squeezed. -->
        <div class="flex flex-wrap gap-x-[8px] gap-y-4 mt-[16px]">
          <div class="flex flex-col w-[206px] shrink-0">
            <div class="flex justify-between items-center">
              <h5>Date de début *</h5>
            </div>
            <div class="flex gap-2 w-full">
              <div class="flex-1">
                <DateInput
                  v-model="startDateModel"
                  placeholder="Choisir"
                  :teleport="true"
                  :error="(props.error && !startDateModel) || (error && !startDateModel)"
                >
                </DateInput>
                <span
                  v-if="(!!props.error && !startDateModel) || (error && !startDateModel)"
                  class="text-primary-red-color body_regular"
                >
                  Ce champ est obligatoire
                </span>
              </div>
              <div class="">
                <TimePicker
                  v-model="startTimeModel"
                  :error="!!error && !!(!startTimeModel && endTimeModel)"
                  default-value="09:00"
                />
              </div>
            </div>
          </div>
          <div class="flex flex-col w-[206px] shrink-0">
            <div class="flex flex-col relative">
              <h5>Date de fin *</h5>
            </div>
            <div class="flex gap-2">
              <div class="flex-1">
                <DateInput
                  v-model="endDateModel"
                  placeholder="Choisir"
                  :teleport="true"
                  :error="(props.error && !endDateModel) || (error && !endDateModel)"
                >
                </DateInput>
                <span
                  v-if="(!!props.error && !endDateModel) || (error && !endDateModel)"
                  class="text-primary-red-color body_regular"
                >
                  Ce champ est obligatoire
                </span>
              </div>
              <div>
                <TimePicker v-model="endTimeModel" :error="errorTime ?? undefined" default-value="17:00" />
              </div>
            </div>
          </div>
          <div class="flex flex-col flex-1 min-w-[240px]">
            <h5>Fuseau horaire</h5>
            <TimezoneDropdown v-model="timezoneModel" :reference-date="startDateModel" />
          </div>
        </div>
        <div
          v-if="error && startDateModel && endDateModel"
          class="text-primary-red-color body_regular flex justify-center"
        >
          <span v-if="!!error && !!(!startTimeModel && endTimeModel)">
            L'heure de début est obligatoire si l'heure de fin est renseignée.
          </span>
          <span v-if="errorTime && !endTimeModel">
            L'heure de fin est obligatoire si l'heure de début est renseignée.
          </span>
        </div>

        <div
          v-if="
            trainingDurationHoursModel &&
            endTimeModel &&
            startTimeModel &&
            trainingDurationHoursModel !== (timeToSeconds(endTimeModel) - timeToSeconds(startTimeModel)) / 3600
          "
          class="flex gap-1 text-secondary-color mt-2 items-center"
        >
          <SvgIcon name="info" class="h-[16px] w-[16px]"></SvgIcon>
          <span class="body_regular">La durée semble incohérente avec les heures de début et de fin. </span>
        </div>

        <div
          v-if="
            props.trainingDuration &&
            (props.timeslotsDuration ?? 0) + (trainingDurationHoursModel ?? 0) > props.trainingDuration
          "
          class="flex gap-1 text-secondary-color mt-2 items-center"
        >
          <SvgIcon name="info" class="h-[16px] w-[16px]"></SvgIcon>
          <span class="body_regular"
            >La somme des durées étapes semble être différente de la durée théorique de la formation ({{
              props?.trainingDuration ?? 0
            }}h).</span
          >
        </div>
        <div class="flex flex-wrap gap-4 mt-[16px]">
          <div class="flex flex-col w-[206px] shrink-0">
            <h5>Durée (heures) *</h5>
            <div class="relative">
              <CommonInput
                v-model="trainingDurationHoursModel"
                placeholder="A définir"
                type="number"
                min="0"
                :error="(props.error && !trainingDurationHoursModel) || (error && !trainingDurationHoursModel)"
              />
              <span
                v-if="(!!props.error && !trainingDurationHoursModel) || (error && !trainingDurationHoursModel)"
                class="text-primary-red-color body_regular absolute"
              >
                Ce champ est obligatoire
              </span>
            </div>
          </div>
          <div class="flex flex-col w-[206px] shrink-0">
            <h5>Format</h5>
            <SimpleDropdown
              v-model="format"
              label="value"
              :options="SessionFormats"
              track-by="name"
              :delete="false"
              hide-selected
            >
            </SimpleDropdown>
          </div>
          <div
            v-if="props.tmpSessionTimeslots?.format !== SessionItemFormatEnum.ONLINE"
            class="flex flex-col flex-1 min-w-[240px] gap-2"
          >
            <h5>Type de classe</h5>

            <div class="flex gap-2 text-secondary-color">
              <label class="flex items-center gap-2 cursor-pointer">
                <RadioInput :checked="!isVisioconference" @update:model-value="updateLocationType(null)"></RadioInput>
                <span class="text-main-color">Présentiel</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <RadioInput
                  :checked="isVisioconference"
                  @update:model-value="updateLocationType(VISIOCONFERENCE_LOCATION)"
                >
                </RadioInput>
                <span class="text-main-color">Visioconférence</span>
              </label>
            </div>
          </div>
        </div>
        <div class="flex gap-[8px] mt-[16px]">
          <div v-if="!isVisioconference" class="flex flex-col w-full min-w-0">
            <h5>Lieu</h5>
            <LocationDropdown
              v-model="tmpLocation"
              :location="props.tmpSessionTimeslots?.selectedLocation ?? undefined"
              :options="locationsStore.locations"
              remove
              placeholder="Saisir une adresse ou sélectionner un lieu"
              lazy-load
              :servicesAPI="locationsServicesAPI"
              :store="locationsStore"
              store-object="locations"
            >
            </LocationDropdown>
          </div>
          <div v-else class="flex flex-col w-full min-w-0">
            <h5>Lien de visioconférence *</h5>
            <CommonInput
              v-model="props.tmpSessionTimeslots.videoCallUrl"
              placeholder="https://teams.microsoft.com/meet/1234567890123"
              style="text-align: start"
              :error="error && !isValidUrl(props.tmpSessionTimeslots?.videoCallUrl)"
            />
            <span
              v-if="error && !isValidUrl(props.tmpSessionTimeslots?.videoCallUrl)"
              class="text-primary-red-color body_regular"
            >
              {{
                !props.tmpSessionTimeslots?.videoCallUrl
                  ? "Ce champ est obligatoire"
                  : "Veuillez saisir un lien valide (ex: https://meet.google.com/...)"
              }}
            </span>
          </div>
        </div>
        <div class="flex gap-[8px] mt-[16px] items-start">
          <div class="flex flex-col basis-1/2">
            <h5>Nom de l’étape</h5>
            <CommonInput v-model="stepName" style="text-align: start" placeholder="Saisir un nom d’étape" />
          </div>
          <div class="flex flex-col basis-1/2">
            <h5>Formateurs</h5>
            <TagDropdown
              v-model="newInternalTrainer"
              multiple
              searchable
              :loading="!usersStore.companyUsers"
              placeholder="Saisissez leur nom(s)"
              :options="usersStore.companyUsers || []"
              label="searchFields"
              track-by="displayValue"
              :store="usersStore"
              store-object="companyUsers"
              :services-a-p-i="companyUsersServicesAPI"
            >
              <template #tag="{ option }">
                <span>{{ option.displayValue }}</span>
              </template>
              <template #option="{ option }">
                <span>{{ option.displayValue }}</span>
              </template>
            </TagDropdown>
          </div>
        </div>
      </div>
      <div class="flex justify-end gap-2">
        <AppButton type="link" icon="close" label="Annuler" variant="error" @click="close"> </AppButton>
        <FlatLoadButton name-icon="tick" :text-confirm="create ? 'Ajouter' : 'Enregistrer'" :async-action="save">
        </FlatLoadButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "assets/scss/texts";

.edit_card__container {
  border: 1px solid var(--grey-lightest);
  background: var(--background-main);
  border-radius: 8px;

  &[data-error="true"] {
    border-color: var(--red-main);
  }
}

.session_step__date {
  @apply p-[8px];
  @extend .heading_3_demi;
}

.step__container {
  @apply flex flex-col px-6;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 16px;
}

.dates__collective_form {
  @extend .body_regular;

  h5 {
    @apply text-secondary-color pb-[4px];
  }

  input {
    @extend .body_regular;
  }
}

.input__default {
  &[data-error="true"] {
    .input__default {
      border-color: var(--red-main);
    }
  }
}
</style>
